import { create } from 'zustand';
import { NewsletterState, NewsletterStats, NewsletterPostsStats, NewsletterBenchmarks, NewsletterInsights, NewsletterPostClicks, NewsletterHistoryStats } from '@/types/newsletter';
import { newsletterService } from '@/services/newsletterService';

interface NewsletterStore extends NewsletterState {
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentStats: (stats: NewsletterStats | null) => void;
  setSelectedPublication: (publicationId: string | null) => void;
  setPostsStats: (postsStats: NewsletterPostsStats | null) => void;
  setBenchmarks: (benchmarks: NewsletterBenchmarks | null) => void;
  setInsights: (insights: NewsletterInsights | null) => void;
  setPostClicks: (postClicks: NewsletterPostClicks | null) => void;
  setHistoryStats: (historyStats: NewsletterHistoryStats[]) => void;

  loadCustomerStats: (customerId: string, period?: string, customStartDate?: string, customEndDate?: string) => Promise<void>;
  loadCustomerPublications: (customerId: string) => Promise<void>;
  loadPublicationHistory: (publicationId: string) => Promise<void>;
  loadPublicationPostsStats: (publicationId: string, period?: string, customStartDate?: string, customEndDate?: string) => Promise<void>;
  loadPostBenchmarks: (postId: string) => Promise<void>;
  loadPostInsights: (postId: string) => Promise<void>;
  loadPostClicks: (postId: string) => Promise<void>;
}

export const useNewsletterStore = create<NewsletterStore>((set) => ({
  publications: [],
  currentStats: null,
  selectedPublicationId: null,
  postsStats: null,
  benchmarks: null,
  insights: null,
  postClicks: null,
  historyStats: [],
  isLoading: false,
  error: null,

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setCurrentStats: (currentStats) => set({ currentStats }),
  setSelectedPublication: (selectedPublicationId) => set({ selectedPublicationId }),
  setPostsStats: (postsStats) => set({ postsStats }),
  setBenchmarks: (benchmarks) => set({ benchmarks }),
  setInsights: (insights) => set({ insights }),
  setPostClicks: (postClicks) => set({ postClicks }),
  setHistoryStats: (historyStats) => set({ historyStats }),

  loadCustomerStats: async (customerId: string, period?: string, customStartDate?: string, customEndDate?: string) => {
    set({ isLoading: true, error: null });
    try {
      const stats = await newsletterService.getCustomerPublicationStats(customerId, period, customStartDate, customEndDate);
      set({ currentStats: stats, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load customer stats';
      set({ error: errorMessage, isLoading: false });
    }
  },

  loadCustomerPublications: async (customerId: string) => {
    set({ isLoading: true, error: null });
    try {
      const publications = await newsletterService.getCustomerPublications(customerId);
      set({ publications, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load publications';
      set({ error: errorMessage, isLoading: false });
    }
  },

  loadPublicationHistory: async (publicationId: string) => {
    set({ isLoading: true, error: null });
    try {
      const history = await newsletterService.getPublicationStatsHistory(publicationId);
      set({ historyStats: history, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load publication history';
      set({ error: errorMessage, isLoading: false });
    }
  },

  loadPublicationPostsStats: async (publicationId: string, period?: string, customStartDate?: string, customEndDate?: string) => {
    set({ isLoading: true, error: null });
    try {
      const postsStats = await newsletterService.getPublicationPostsStats(publicationId, period, customStartDate, customEndDate);
      set({ postsStats, isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load posts stats';
      set({ error: errorMessage, isLoading: false });
    }
  },

  loadPostBenchmarks: async (postId: string) => {
    try {
      const benchmarks = await newsletterService.getPostBenchmarks(postId);

      // Update the post in postsStats with the loaded benchmarks
      set((state) => {
        if (!state.postsStats) return state;

        const updatedPosts = state.postsStats.posts.map(post =>
          post.id === postId ? { ...post, benchmarks } : post
        );

        return {
          ...state,
          postsStats: { ...state.postsStats, posts: updatedPosts },
          benchmarks
        };
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load post benchmarks';
      console.error(errorMessage, error);
    }
  },

  loadPostInsights: async (postId: string) => {
    try {
      const insights = await newsletterService.getPostInsights(postId);

      // Update the post in postsStats with the loaded insights
      set((state) => {
        if (!state.postsStats) return state;

        const updatedPosts = state.postsStats.posts.map(post =>
          post.id === postId ? { ...post, insights } : post
        );

        return {
          ...state,
          postsStats: { ...state.postsStats, posts: updatedPosts },
          insights
        };
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load post insights';
      console.error(errorMessage, error);
    }
  },

  loadPostClicks: async (postId: string) => {
    try {
      const postClicks = await newsletterService.getPostClicks(postId);

      // Update the post in postsStats with the loaded clicks
      set((state) => {
        if (!state.postsStats) return state;

        const updatedPosts = state.postsStats.posts.map(post =>
          post.id === postId ? { ...post, clicks: postClicks } : post
        );

        return {
          ...state,
          postsStats: { ...state.postsStats, posts: updatedPosts },
          postClicks
        };
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load post clicks';
      console.error(errorMessage, error);
    }
  },
}));